import React, { useState } from 'react';

import styled from 'styled-components';

import AiCiSectionIcon from 'components/AiCompetitiveIntelligence/AiCiSectionIcon';
import MarketPositionSummary from 'components/AiCompetitiveIntelligence/MarketPositionSummary';
import RankingsTable from 'components/AiCompetitiveIntelligence/RankingsTable';
import PillSwitcher from 'components/lib/PillSwitcher';
import { AICompetitiveIntelligenceRankingRow, SpeedToLeadMetrics } from 'types/recommendations';

import { SPEED_TO_LEAD_RANKING_COPY, SPEED_TO_LEAD_RANKING_VIEW, SpeedToLeadRankingView } from './constants';
import { formatOrdinal, OverallInsight } from './speedToLeadOverallUtils';
import { DashboardCard, SectionHeading, SectionTitle, SubsectionTitle } from './SpeedToLeadSection.styles';

const RankingTableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
`;

const RankingDescriptionText = styled.div`
  display: -webkit-box;
  height: 36px;
  margin-top: 2px;
  overflow: hidden;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 18px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

type ScoreRow = SpeedToLeadMetrics['quality_vs_speed']['rows'][number];

type Props = {
  categoryRecommendationIds: Partial<Record<SpeedToLeadRankingView, number>>;
  competitorsCount: number;
  insight: OverallInsight;
  onCategoryDetailsOpen: (recommendationId: number) => void;
  primaryCommunityId: number | null;
  primaryCommunityName: string | null;
  scoreRows: ScoreRow[];
};

const getScoreRank = (rows: ScoreRow[], field: 'speed_score' | 'quality_score', communityId: number | null) => {
  const rankedRows = [...rows].sort((left, right) => Number(right[field]) - Number(left[field]));
  const index = rankedRows.findIndex((row) => row.community_id === communityId);
  return index >= 0 ? index + 1 : null;
};

const SpeedToLeadRanking = ({
  categoryRecommendationIds,
  competitorsCount,
  insight,
  onCategoryDetailsOpen,
  primaryCommunityId,
  primaryCommunityName,
  scoreRows,
}: Props) => {
  const [rankingView, setRankingView] = useState<SpeedToLeadRankingView>(SPEED_TO_LEAD_RANKING_VIEW.OVERALL);
  const overallRank =
    insight.rankingRows.find((row) => row.community === primaryCommunityName)?.rank ?? insight.data.ranking ?? null;
  const competingCommunitiesCount = competitorsCount + 1;
  const marketPositionSubtitle = `Position within ${competingCommunitiesCount} competing ${
    competingCommunitiesCount === 1 ? 'community' : 'communities'
  }`;
  const rankingItems = [
    { key: SPEED_TO_LEAD_RANKING_VIEW.OVERALL, label: <>Overall&nbsp; {formatOrdinal(overallRank)}</> },
    {
      key: SPEED_TO_LEAD_RANKING_VIEW.SPEED,
      label: <>Speed&nbsp; {formatOrdinal(getScoreRank(scoreRows, 'speed_score', primaryCommunityId))}</>,
    },
    {
      key: SPEED_TO_LEAD_RANKING_VIEW.QUALITY,
      label: <>Quality&nbsp; {formatOrdinal(getScoreRank(scoreRows, 'quality_score', primaryCommunityId))}</>,
    },
  ];
  const selectedRankingField = rankingView === SPEED_TO_LEAD_RANKING_VIEW.SPEED ? 'speed_score' : 'quality_score';
  const selectedCategoryRecommendationId = categoryRecommendationIds[rankingView];
  const selectedRankingRows: AICompetitiveIntelligenceRankingRow[] =
    rankingView === SPEED_TO_LEAD_RANKING_VIEW.OVERALL
      ? insight.rankingRows
      : [...scoreRows]
          .sort((left, right) => Number(right[selectedRankingField]) - Number(left[selectedRankingField]))
          .map((row, index) => {
            const explanation =
              rankingView === SPEED_TO_LEAD_RANKING_VIEW.SPEED ? row.speed_explanation : row.quality_explanation;
            return {
              rank: index + 1,
              community: row.community_name,
              community_id: row.community_id,
              score: Number(row[selectedRankingField]),
              explanation: explanation ?? 'No explanation is available for this community.',
            };
          });

  return (
    <DashboardCard>
      <MarketPositionSummary
        data={insight.data}
        title="Speed to Lead"
        subtitle={marketPositionSubtitle}
        iconSrc="/icons/speed-to-lead-timer.svg"
        iconVariant="speed-to-lead"
      />
      {insight.rankingRows.length > 0 && (
        <>
          <SectionHeading>
            <AiCiSectionIcon src="/icons/format-list-bulleted.svg" />
            <SectionTitle>Ranking</SectionTitle>
          </SectionHeading>
          <PillSwitcher items={rankingItems} activeKey={rankingView} onChange={setRankingView} />
          <RankingTableSection>
            <div>
              <SubsectionTitle>{SPEED_TO_LEAD_RANKING_COPY[rankingView].title}</SubsectionTitle>
              <RankingDescriptionText>{SPEED_TO_LEAD_RANKING_COPY[rankingView].description}</RankingDescriptionText>
            </div>
            <RankingsTable
              rows={selectedRankingRows}
              primaryCommunityName={primaryCommunityName}
              showPrimaryDetailsButton={selectedCategoryRecommendationId != null}
              onPrimaryDetailsClick={
                selectedCategoryRecommendationId == null
                  ? undefined
                  : () => onCategoryDetailsOpen(selectedCategoryRecommendationId)
              }
              bare
            />
          </RankingTableSection>
        </>
      )}
    </DashboardCard>
  );
};

export default SpeedToLeadRanking;
